if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.checkoutRedirect = this.dataset.checkoutRedirect === 'true';

        if (document.querySelector('cart-drawer') && !this.checkoutRedirect) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.leavingForCheckout = false;
        this.error = false;

        // Ensure the active flavor id is enabled + current before FormData
        // (disabled inputs are omitted; first-click can otherwise send no id).
        const idInput = this.variantIdInput;
        if (idInput) idInput.disabled = false;
        const productInfo =
          this.closest('product-info') || document.querySelector('product-info');
        const flavorBtn = productInfo?.querySelector(
          'clc-custom-variants .clc-option-button.is-active[data-variant-id]:not([data-sold-out="true"])'
        );
        if (idInput && flavorBtn?.dataset.variantId) {
          idInput.value = flavorBtn.dataset.variantId;
        }
        const activeQty = productInfo?.querySelector(
          'clc-custom-quantity-option .clc-option-button.is-active'
        );
        const qtyOption = activeQty?.closest('clc-custom-quantity-option');
        if (qtyOption && typeof qtyOption.applyQuantity === 'function' && activeQty?.dataset.quantity) {
          qtyOption.applyQuantity(productInfo, activeQty.dataset.quantity);
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const buildAddConfig = () => {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(this.form);
          if (this.cart && !this.checkoutRedirect) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          return { config, formData };
        };

        const runAdd = () => {
          const { config } = buildAddConfig();
          return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        };

        const goCheckoutWithRetry = async (firstResponse) => {
          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          const variantId = String(this.variantIdInput?.value || '');
          if (!variantId) {
            this.error = true;
            this.handleErrorMessage('The product was not added to the cart. Please try again.');
            return;
          }

          let response = firstResponse;
          let ready = { ok: false, reason: 'pouches-missing' };

          // Verify via /cart.js; retry add if pouches did not stick (first-visit race).
          for (let attempt = 0; attempt < 3; attempt += 1) {
            if (attempt > 0) {
              response = await runAdd();
              if (response.status) break;
            }
            ready = await this.waitForCheckoutReady(variantId, 12);
            if (ready.ok) break;
          }

          if (!ready.ok) {
            this.error = true;
            this.handleErrorMessage(
              ready.reason === 'gifts-only'
                ? 'Only free gifts were added. Please try Checkout again.'
                : 'The product was not added to the cart. Please try again.'
            );
            return;
          }

          if (!this.error) {
            await this.publishCartUpdateWithTimeout({
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          }

          // Gift apps may race after publish — confirm pouches still present; one more add if not.
          ready = await this.waitForCheckoutReady(variantId, 12);
          if (!ready.ok) {
            response = await runAdd();
            if (!response.status) {
              ready = await this.waitForCheckoutReady(variantId, 12);
            }
          }
          if (!ready.ok) {
            this.error = true;
            this.handleErrorMessage(
              ready.reason === 'gifts-only'
                ? 'Only free gifts were added. Please try Checkout again.'
                : 'The product was not added to the cart. Please try again.'
            );
            return;
          }

          this.error = false;
          this.leavingForCheckout = true;
          window.location.href = window.routes.checkout_url || '/checkout';
        };

        this.ensureCartSession()
          .then(() => runAdd())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: this.variantIdInput?.value,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (this.checkoutRedirect) {
              return goCheckoutWithRetry(response);
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              this.publishCartUpdateWithTimeout({
                source: 'product-form',
                productVariantId: this.variantIdInput?.value,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.leavingForCheckout) return;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      cartItemIsGifted(item) {
        return Boolean(item?.properties && item.properties._Gifted != null);
      }

      cartIsGiftsOnly(cart) {
        const items = cart?.items || [];
        return items.length > 0 && items.every((item) => this.cartItemIsGifted(item));
      }

      async ensureCartSession() {
        const root = window.Shopify?.routes?.root || '/';
        try {
          await fetch(`${root}cart.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
        } catch (_err) {
          /* ignore */
        }
      }

      async publishCartUpdateWithTimeout(data, timeoutMs = 2500) {
        try {
          await Promise.race([
            publish(PUB_SUB_EVENTS.cartUpdate, data),
            new Promise((resolve) => setTimeout(resolve, timeoutMs)),
          ]);
        } catch (_err) {
          /* gift-app subscribers must not block checkout */
        }
      }

      // Empty variant id must fail closed (never treat as ready).
      waitForCartVariant(variantId, attempts = 16) {
        return this.waitForCheckoutReady(variantId, attempts).then((result) => result.ok);
      }

      // A+B: wait until intended pouch is present and cart is not gifts-only.
      waitForCheckoutReady(variantId, attempts = 16) {
        const wanted = String(variantId || '');
        if (!wanted) return Promise.resolve({ ok: false, reason: 'missing-variant' });
        const root = window.Shopify?.routes?.root || '/';
        const check = (attempt) =>
          fetch(`${root}cart.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          })
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
            .then((cart) => {
              if (cart) {
                const items = cart.items || [];
                const hasPouch = items.some((item) => String(item.variant_id) === wanted);
                const giftsOnly = this.cartIsGiftsOnly(cart);
                if (hasPouch && !giftsOnly) return { ok: true, cart };
                if (attempt + 1 >= attempts) {
                  return {
                    ok: false,
                    reason: giftsOnly ? 'gifts-only' : 'pouches-missing',
                    cart,
                  };
                }
              } else if (attempt + 1 >= attempts) {
                return { ok: false, reason: 'pouches-missing' };
              }
              return new Promise((resolve) => setTimeout(resolve, 250)).then(() => check(attempt + 1));
            });
        return check(0);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
